import React from "react";
import { PropTypes } from "prop-types";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import moment from "moment";

import history from "components/common/navigation/AppHistory";
import FreecopyUniqueCodeForm, {
  DURATIONS_TYPE,
} from "components/freecopy/FreecopyUniqueCodeForm";
import Spinner from "../utils/Spinner";
import OfferAPI from "../../api/OfferAPI";
import { PROJECTS_ROUTE } from "../routes/routes";

import "css/admin/smartlist.scss";

export function FreecopyUniqueCodePage({ match }) {
  const { t } = useTranslation("editor-freecopy", "editor-errors");
  const { projectSlug } = useParams();
  const queryClient = useQueryClient();

  const isEdit = match?.params?.action === "edit" && match?.params?.id;

  const showSuccessMessage = () => toast.success(t("editor-notif:saveSuccess"));

  const showErrorMessage = (errors) => toast.error(errors);

  const getOfferPlansQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOfferPlans,
    params: [{ projectSlug }],
  });

  const getFreecopyUniqueCodeQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getFreecopyUniqueCode,
    params: [
      {
        uuid: match?.params?.id,
        projectSlug,
      },
    ],
  });

  const { data: offerPlansData } = useQuery(getOfferPlansQuery);

  const { data: freecopyCodeData, isLoading: isLoadingData } = useQuery({
    ...getFreecopyUniqueCodeQuery,
    onError: () => {
      showErrorMessage(t("editor-notif:fetchUniqueCodeError"));
      history.push(`${PROJECTS_ROUTE}/${projectSlug}/freecopy`);
    },
    enabled: Boolean(isEdit),
  });

  const onError = () => {
    showErrorMessage(t("editor-notif:saveError"));
  };

  const onErrorForm = (errors) => {
    const errorDetails = errors.join(", ");
    showErrorMessage(
      `${t("editor-notif:saveError")}${
        errorDetails ? ` : ${errorDetails}` : ""
      }`,
    );
  };

  const onSuccess = () => {
    if (isEdit) {
      queryClient.invalidateQueries(getFreecopyUniqueCodeQuery.queryKey);
    }
    showSuccessMessage();

    const getFreecopiesUniqueCodeQuery = OfferAPI.generateQuery({
      fn: OfferAPI.getFreecopiesUniqueCode,
      params: [{ projectSlug }],
    });

    queryClient.invalidateQueries(getFreecopiesUniqueCodeQuery.queryKey);

    history.push(`${PROJECTS_ROUTE}/${projectSlug}/freecopy`);
  };

  const submitValid = (item) => {
    const errorMessages = [];

    if (!item.label) {
      errorMessages.push(t("editor-errors:labelCodeNull"));
    }

    if (!isEdit) {
      if (!item.code) {
        errorMessages.push(t("editor-errors:codeNull"));
      } else if (!item.validCode) {
        errorMessages.push(t("editor-errors:codeExist"));
      }

      if (!item.offer_plan_uuid) {
        errorMessages.push(t("editor-errors:offerNull"));
      }
    }

    if (item.durationType === DURATIONS_TYPE.DURATION_DAYS) {
      if (item.days_duration <= 0) {
        errorMessages.push(t("editor-errors:durationDaysNull"));
      }
    } else if (!item.usage_limit_date) {
      errorMessages.push(t("editor-errors:limiteDateAccesNull"));
    } else if (moment(item.usage_limit_date).isBefore(moment())) {
      errorMessages.push(t("editor-errors:limiteAccesMustBeInFuture"));
    }

    if (item.max_activations <= 0) {
      errorMessages.push(t("editor-errors:maxActivationNull"));
    }

    if (!isEdit) {
      if (!item.activation_limit_date) {
        errorMessages.push(t("editor-errors:limiteDateActivationNull"));
      } else if (moment(item.activation_limit_date).isBefore(moment())) {
        errorMessages.push(t("editor-errors:limiteActivationMustBeInFuture"));
      }
    }

    return errorMessages;
  };

  const updateFreecopyCode = useMutation({
    mutationFn: ({ uuid, data, projectSlug }) =>
      OfferAPI.updateFreecopyUniqueCode({ uuid, data, projectSlug }),
    onSuccess,
    onError,
  });

  const createFreecopyCode = useMutation({
    mutationFn: ({ data, projectSlug }) =>
      OfferAPI.createFreecopyUniqueCode({ data, projectSlug }),
    onSuccess,
    onError,
  });

  const onCreate = (item) => {
    const errors = submitValid(item);

    if (errors.length > 0) {
      onErrorForm(errors);
      return;
    }
    createFreecopyCode.mutate({ data: item, projectSlug });
  };

  const onUpdate = (item) => {
    const errors = submitValid(item);

    if (errors.length > 0) {
      onErrorForm(errors);
      return;
    }

    updateFreecopyCode.mutate({ uuid: item.uuid, data: item, projectSlug });
  };

  const freecopyCode = isEdit
    ? freecopyCodeData
    : {
        enabled: false,
        max_activations: 0,
        days_duration: 0,
        usage_limit_date: null,
      };

  const onSubmit = isEdit ? onUpdate : onCreate;

  const loadingEdit = isLoadingData && isEdit;

  const isLoading = Boolean(
    updateFreecopyCode.isLoading || createFreecopyCode.isLoading,
  );

  return (
    <div className="editor-freecopy-edit-container">
      {loadingEdit ? (
        <div className="admin-user-form_spinner-container">
          <Spinner />
        </div>
      ) : (
        <FreecopyUniqueCodeForm
          item={freecopyCode}
          onSubmit={onSubmit}
          editMode={isEdit}
          offerPlans={offerPlansData?.data}
          loading={isLoading}
        />
      )}
    </div>
  );
}

FreecopyUniqueCodePage.propTypes = {
  match: PropTypes.object.isRequired,
};

export default FreecopyUniqueCodePage;
